import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, CheckCircle2, Loader2, Upload, Camera, Trash2, FileText, XCircle, AlertCircle
} from 'lucide-react';

// Éléments partagés par les deux pages publiques du dossier d'adhésion :
// la page de conditions en libre-service (/conditions-assurance) et la page
// du dossier personnel d'un candidat (/dossier-candidat/:token).
// Une seule définition des couleurs, du gabarit et de la carte de formule,
// pour que les deux pages ne puissent pas diverger visuellement.

// Nom affiché sur toutes les pages publiques. Un seul endroit à modifier.
export const NOM_SOCIETE = 'Cabinet Conseil';
export const INITIALES_SOCIETE = 'CC';

export const NAVY = '#2d3f7f';
export const NAVY_DARK = '#1d2b57';
export const NAVY_SOFT = '#eef2fb';

export const StyleAssu = () => (
  <style>{`
    /* Verrou anti-débordement : tant qu'un seul élément dépasse la largeur de
       l'écran, le téléphone dézoome toute la page. On borne donc la racine. */
    html, body { max-width:100%; overflow-x:hidden; }
    .dca-page { width:100%; max-width:100vw; overflow-x:hidden; }
    .dca-page img, .dca-page table, .dca-page iframe { max-width:100%; }
    .dca-wrap { max-width: 780px; margin: 0 auto; padding: 0 16px 64px; box-sizing:border-box; }
    .dca-wrap, .dca-card, .dca-formule { overflow-wrap:anywhere; min-width:0; }
    .dca-card, .dca-formule { box-sizing:border-box; }
    .dca-tab { max-width:100%; table-layout:fixed; }
    .dca-card { background:#fff; border:1px solid #e7eaf3; border-radius:16px; padding:20px; margin-bottom:16px; }
    .dca-formule { border:2px solid #e7eaf3; border-radius:16px; padding:18px; margin-bottom:14px;
                   transition:border-color .15s, box-shadow .15s, background .15s; background:#fff; }
    .dca-formule.cliquable { cursor:pointer; }
    .dca-formule.cliquable:hover { border-color:${NAVY}66; }
    .dca-formule.on { border-color:${NAVY}; background:${NAVY_SOFT}; box-shadow:0 6px 20px rgba(45,63,127,.13); }
    .dca-tab { width:100%; border-collapse:collapse; font-size:14px; margin-top:12px; }
    .dca-tab td { padding:8px 10px; border-top:1px solid #e7eaf3; vertical-align:top; }
    .dca-tab td:first-child { color:#5f6b85; font-weight:600; width:45%; }
    .dca-bareme { background:#fff; border-radius:10px; overflow:hidden; min-width:340px; }
    .dca-bareme th { padding:8px 10px; text-align:left; font-size:12px; color:#5f6b85;
                     background:#f2f4fa; font-weight:700; white-space:nowrap; }
    .dca-bareme td { white-space:nowrap; }
    .dca-bareme td:first-child { color:#2f3648; font-weight:600; width:auto; }
    .dca-doc { border:1px solid #e7eaf3; border-radius:14px; padding:15px; margin-bottom:12px; }
    .dca-doc.manque { border-color:#e0a3a3; background:#fdf6f6; }
    .dca-btn { border:none; border-radius:11px; padding:13px 20px; font-size:15px; font-weight:700;
               cursor:pointer; display:inline-flex; align-items:center; gap:8px; }
    /* 16px minimum : en dessous, iOS zoome sur le champ à chaque saisie. */
    .dca-champ { width:100%; padding:12px 14px; border:1.5px solid #ddd; border-radius:10px;
                 font-size:16px; box-sizing:border-box; }
    .dca-entete { padding:30px 16px 34px; }
    .dca-entete h1 { margin:12px 0 6px; font-size:25px; line-height:1.25; }
    .dca-total { font-size:38px; font-weight:800; line-height:1.2; margin:4px 0 2px; }
    .dca-defile { overflow-x:auto; -webkit-overflow-scrolling:touch; }
    .dca-fichier { display:flex; align-items:center; gap:9px; justify-content:space-between;
                   background:${NAVY_SOFT}; border-radius:10px; padding:9px 12px; margin-top:9px; font-size:13.5px; }
    .dca-spin { animation: dca-rot 1s linear infinite; }
    @keyframes dca-rot { to { transform: rotate(360deg); } }
    /* --- Téléphone --- */
    @media (max-width: 620px) {
      .dca-wrap { padding:0 12px 48px; }
      .dca-card, .dca-formule { padding:14px; border-radius:14px; }
      .dca-entete { padding:22px 12px 26px; }
      .dca-entete h1 { font-size:21px; margin:9px 0 5px; }
      .dca-total { font-size:30px; }
      .dca-btn { width:100%; justify-content:center; padding:14px 18px; }
      .dca-doc { padding:13px; }
      /* Les deux boutons d'envoi passent l'un sous l'autre, pleine largeur. */
      .dca-doc label { width:100%; justify-content:center; box-sizing:border-box; }
      .dca-fichier { font-size:13px; }

      /* Tableau intitulé / valeur : la valeur passe sous son intitulé. */
      .dca-tab:not(.dca-bareme) { font-size:14px; }
      .dca-tab:not(.dca-bareme) tr { display:block; border-top:1px solid #e7eaf3; padding:8px 0; }
      .dca-tab:not(.dca-bareme) td { display:block; border:none; padding:0; width:auto; }
      .dca-tab:not(.dca-bareme) td:first-child { color:#5f6b85; font-size:12.5px; font-weight:700;
                                                 text-transform:uppercase; letter-spacing:.3px; margin-bottom:2px; }

      /* Barème : chaque ligne devient une petite carte, l'intitulé de colonne à
         gauche et la valeur à droite. Plus aucun défilement horizontal. */
      .dca-bareme { min-width:0; display:block; font-size:14px; }
      .dca-bareme thead { display:none; }
      .dca-bareme tbody { display:block; }
      .dca-bareme tr { display:block; border:1px solid #e7eaf3; border-radius:11px;
                       padding:9px 12px; margin-bottom:8px; background:#fff; }
      .dca-bareme td { display:flex; justify-content:space-between; align-items:baseline;
                       gap:12px; border:none; padding:3px 0; width:auto; white-space:normal; }
      .dca-bareme td::before { content:attr(data-col); color:#5f6b85; font-weight:600;
                               font-size:12.5px; flex:0 0 auto; }
      .dca-bareme td:first-child { border-bottom:1px solid #eef0f6; padding-bottom:6px;
                                   margin-bottom:4px; font-weight:800; width:auto; }
    }
  `}</style>
);

export const EnteteAssu = ({ titre, sousTitre }) => (
  <div className="dca-entete" style={{
    background: `linear-gradient(125deg, ${NAVY_DARK} 0%, ${NAVY} 65%, #3d55b0 100%)`,
    color: '#fff', marginBottom: 22
  }}>
    <div style={{ maxWidth: 780, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontWeight: 800, fontSize: 15, opacity: 0.9 }}>
        <ShieldCheck size={19} /> {NOM_SOCIETE}
      </div>
      <h1>{titre}</h1>
      <div style={{ fontSize: 14.5, opacity: 0.88 }}>{sousTitre}</div>
    </div>
  </div>
);

// translate="no" : la traduction automatique de Chrome rendait « Fixe » par
// « Réparer » et « On déduit » par « Sur déduit ». Sur une page qui engage une
// rémunération, le texte doit rester exactement celui qu'on a écrit.
export const PageAssu = ({ titre, sousTitre, children }) => (
  <div
    translate="no"
    className="notranslate dca-page"
    style={{
      background: '#f6f7fb', minHeight: '100vh', color: '#2f3648',
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}
  >
    <StyleAssu />
    <EnteteAssu titre={titre} sousTitre={sousTitre} />
    <div className="dca-wrap">{children}</div>
  </div>
);

// Carte d'une formule : résumé, lignes intitulé/valeur, tableaux et conditions.
// Sans `onChoisir`, la carte est en lecture seule (page de conditions).
export const FormuleCarte = ({ formule: f, choisie = false, onChoisir = null, occupe = false }) => (
  <div
    className={`dca-formule${choisie ? ' on' : ''}${onChoisir ? ' cliquable' : ''}`}
    onClick={onChoisir ? () => onChoisir(f.id) : undefined}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
      {onChoisir && (
        <div style={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          border: `2px solid ${choisie ? NAVY : '#c9cfe0'}`, background: choisie ? NAVY : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {choisie && <CheckCircle2 size={14} style={{ color: '#fff' }} />}
        </div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: f.couleur || NAVY }}>{f.nom}</div>
        {f.resume && <div style={{ fontSize: 13.5, color: '#5f6b85', marginTop: 2 }}>{f.resume}</div>}
      </div>
      {occupe && choisie && <Loader2 size={17} className="dca-spin" style={{ color: NAVY }} />}
    </div>

    {f.lignes && f.lignes.length > 0 && (
      <table className="dca-tab">
        <tbody>
          {f.lignes.map((l, i) => (
            <tr key={i}><td>{l.label}</td><td style={{ fontWeight: 600 }}>{l.valeur}</td></tr>
          ))}
        </tbody>
      </table>
    )}

    {(f.tableaux || []).filter(t => t.lignes && t.lignes.length > 0).map((t, ti) => (
      <div key={ti} style={{ marginTop: 14 }}>
        {t.titre && (
          <div style={{ fontSize: 13, fontWeight: 700, color: '#5f6b85', marginBottom: 6 }}>{t.titre}</div>
        )}
        <div className="dca-defile">
          <table className="dca-tab dca-bareme">
            <thead>
              <tr>{t.colonnes.map((c, i) => <th key={i}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {t.lignes.map((ligne, i) => (
                <tr key={i}>
                  {ligne.map((cell, j) => (
                    <td
                      key={j}
                      data-col={t.colonnes[j] || ''}
                      style={j === ligne.length - 1 ? { fontWeight: 800, color: f.couleur || NAVY } : null}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ))}

    {f.conditions && f.conditions.length > 0 && (
      <ul style={{ margin: '12px 0 0', paddingLeft: 20, fontSize: 14, color: '#3d4657' }}>
        {f.conditions.map((c, i) => <li key={i} style={{ marginBottom: 4 }}>{c}</li>)}
      </ul>
    )}
  </div>
);


// ---------------------------------------------------------------------------
// Dépôt des pièces. Utilisé à l'identique par le parcours en une page et par
// la page du dossier personnel, pour que les deux se comportent pareil.
// ---------------------------------------------------------------------------

export const poidsLisible = (o) => {
  if (!o && o !== 0) return '';
  if (o < 1024) return `${o} o`;
  if (o < 1024 * 1024) return `${Math.round(o / 1024)} Ko`;
  return `${(o / 1024 / 1024).toFixed(1)} Mo`;
};

export const PiecesJointes = ({ token, data, onMaj, manquants = [], apiUrl = '' }) => {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState('');
  const inputs = useRef({});

  const requis = data.documents_requis || [];
  const deposes = data.documents || [];
  const fichiersDe = (id) => deposes.filter(d => d.type_doc === id);
  const maxMo = Math.round((data.taille_max_octets || 0) / 1024 / 1024);

  const deposer = async (typeId, fileList) => {
    const fichiers = Array.from(fileList || []);
    if (fichiers.length === 0) return;
    setEnCours(typeId);
    setErreur('');
    try {
      const form = new FormData();
      fichiers.forEach(f => form.append('fichiers', f));
      form.append('type_doc', typeId);
      const res = await axios.post(`${apiUrl}/api/candidats-assu/dossier/${token}/documents`, form);
      onMaj(res.data);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de l\'envoi du fichier');
    } finally {
      setEnCours(null);
      ['fichier', 'photo'].forEach(suffixe => {
        const el = inputs.current[`${typeId}-${suffixe}`];
        if (el) el.value = '';
      });
    }
  };

  const retirer = async (docId) => {
    setEnCours(`doc-${docId}`);
    try {
      const res = await axios.delete(`${apiUrl}/api/candidats-assu/dossier/${token}/documents/${docId}`);
      onMaj(res.data);
      setErreur('');
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors du retrait du fichier');
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="dca-card">
      <div style={{ fontSize: 13.5, color: '#5f6b85', marginBottom: 14 }}>
        Facultatif à ce stade : vous pouvez valider votre formule maintenant et
        envoyer vos pièces plus tard depuis ce même lien. Photo ou PDF,
        {' '}{maxMo} Mo maximum par fichier, appareil photo du téléphone accepté.
      </div>

      {requis.map(t => {
        const fichiers = fichiersDe(t.id);
        const manque = manquants.includes(t.id) && fichiers.length === 0;
        return (
          <div key={t.id} className={`dca-doc${manque ? ' manque' : ''}`}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
              {fichiers.length > 0
                ? <CheckCircle2 size={19} style={{ color: '#2e9e5b', flexShrink: 0, marginTop: 1 }} />
                : <FileText size={19} style={{ color: '#7a839b', flexShrink: 0, marginTop: 1 }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{t.label}</div>
                <div style={{ fontSize: 13, color: '#5f6b85', marginTop: 2 }}>{t.aide}</div>
              </div>
            </div>

            {fichiers.map(fi => (
              <div key={fi.id} className="dca-fichier">
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fi.nom_fichier} <span style={{ color: '#7a839b' }}>· {poidsLisible(fi.taille)}</span>
                </span>
                <button
                  onClick={() => retirer(fi.id)}
                  disabled={enCours === `doc-${fi.id}`}
                  title="Retirer ce fichier"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', padding: 4, display: 'flex' }}
                >
                  {enCours === `doc-${fi.id}` ? <Loader2 size={16} className="dca-spin" /> : <Trash2 size={16} />}
                </button>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 10 }}>
              <label style={boutonFichier}>
                {enCours === t.id ? <Loader2 size={15} className="dca-spin" /> : <Upload size={15} />}
                {fichiers.length > 0 && !t.plusieurs
                  ? 'Remplacer'
                  : (t.plusieurs ? 'Choisir une ou plusieurs photos' : 'Choisir un fichier')}
                <input
                  ref={el => { inputs.current[`${t.id}-fichier`] = el; }}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple={t.plusieurs}
                  disabled={enCours === t.id}
                  onChange={(e) => deposer(t.id, e.target.files)}
                  style={{ display: 'none' }}
                />
              </label>
              <label style={{ ...boutonFichier, borderColor: '#c9cfe0', color: '#5f6b85' }}>
                <Camera size={15} /> Prendre une photo
                <input
                  ref={el => { inputs.current[`${t.id}-photo`] = el; }}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  disabled={enCours === t.id}
                  onChange={(e) => deposer(t.id, e.target.files)}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        );
      })}

      {erreur && (
        <div style={encadreErreur}>{erreur}</div>
      )}

      <div style={{ fontSize: 12.5, color: '#8a94ad', marginTop: 4 }}>
        Vos pièces ne sont consultables que par notre équipe.
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Récapitulatif : ce que le candidat s'apprête à envoyer, ou ce qu'il a envoyé.
// ---------------------------------------------------------------------------

export const Recapitulatif = ({ data, dateEnvoi = null }) => {
  const c = data.candidat || {};
  const requis = data.documents_requis || [];
  const deposes = data.documents || [];
  const formule = (data.formules || []).find(f => f.id === data.formule_id);

  const ligne = (label, valeur) => (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderTop: '1px solid #eef0f6', fontSize: 14.5 }}>
      <div style={{ color: '#5f6b85', flex: '0 0 42%' }}>{label}</div>
      <div style={{ fontWeight: 600, flex: 1, wordBreak: 'break-word' }}>{valeur || '—'}</div>
    </div>
  );

  return (
    <div className="dca-card">
      <div style={{ fontWeight: 800, color: NAVY, fontSize: 16, marginBottom: 6 }}>Votre candidature</div>
      {ligne('Nom et prénom', c.nom)}
      {ligne('Nom dans le CRM', c.nom_crm)}
      {ligne('Numéro WhatsApp', c.telephone_whatsapp)}
      {ligne('Email', c.email_pro)}
      {ligne('Formule choisie', data.formule_nom || (formule && formule.nom))}
      {formule && formule.resume && ligne('En résumé', formule.resume)}
      {dateEnvoi && ligne('Envoyée le', dateEnvoi)}
      {data.signature_nom && ligne('Signée par', data.signature_nom)}

      <div style={{ marginTop: 14, fontWeight: 800, color: NAVY, fontSize: 15 }}>
        Vos pièces ({deposes.length})
      </div>
      {requis.map(t => {
        const f = deposes.filter(d => d.type_doc === t.id);
        return (
          <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 0', borderTop: '1px solid #eef0f6' }}>
            {f.length > 0
              ? <CheckCircle2 size={16} style={{ color: '#2e9e5b', flexShrink: 0, marginTop: 3 }} />
              : <XCircle size={16} style={{ color: '#c9cfe0', flexShrink: 0, marginTop: 3 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t.label}</div>
              <div style={{ fontSize: 13, color: f.length > 0 ? '#5f6b85' : '#a0a7ba' }}>
                {f.length > 0 ? f.map(x => x.nom_fichier).join(', ') : 'À envoyer plus tard'}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Le nom sous lequel le candidat est enregistré dans le CRM. Champ facultatif,
// enregistré à la sortie du champ pour qu'il ne dépende pas de la validation
// finale : un candidat qui referme la page ne le perd pas.
export const ChampNomCrm = ({ token, valeur, apiUrl = '', onMaj, modifiable = true }) => {
  const [saisie, setSaisie] = useState(valeur || '');
  const [etat, setEtat] = useState(null); // 'envoi' | 'ok' | 'erreur'
  const [erreur, setErreur] = useState('');

  // La valeur du serveur fait foi dès qu'elle change (rechargement, autre onglet).
  useEffect(() => { setSaisie(valeur || ''); }, [valeur]);

  const enregistrer = async () => {
    const propre = saisie.trim().replace(/\s+/g, ' ');
    if (propre !== saisie) setSaisie(propre);
    if (propre === (valeur || '')) return;
    setEtat('envoi');
    setErreur('');
    try {
      const res = await axios.post(`${apiUrl}/api/candidats-assu/dossier/${token}/nom-crm`, { nom_crm: propre });
      if (onMaj) onMaj(res.data);
      setEtat('ok');
    } catch (e) {
      setEtat('erreur');
      setErreur(e.response?.data?.error || 'Enregistrement impossible. Réessayez.');
    }
  };

  if (!modifiable) {
    return (
      <div className="dca-card">
        <div style={{ fontWeight: 800, color: NAVY, fontSize: 16, marginBottom: 6 }}>Votre nom dans le CRM</div>
        <div style={{ fontSize: 14.5 }}>{valeur || '—'}</div>
      </div>
    );
  }

  return (
    <div className="dca-card">
      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 5 }}>
        Votre nom dans le CRM <span style={{ color: '#8a94ad', fontWeight: 500 }}>(facultatif)</span>
      </label>
      <div style={{ fontSize: 12.5, color: '#8a94ad', marginBottom: 8 }}>
        Le nom sous lequel vous êtes enregistré dans le CRM. Laissez vide si vous n'avez pas encore de compte.
      </div>
      <input
        className="dca-champ"
        type="text"
        placeholder="Jean D."
        value={saisie}
        maxLength={120}
        onChange={(e) => { setSaisie(e.target.value); setEtat(null); }}
        onBlur={enregistrer}
      />
      {etat === 'envoi' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#5f6b85', fontSize: 13.5, marginTop: 8 }}>
          <Loader2 size={15} className="dca-spin" /> Enregistrement…
        </div>
      )}
      {etat === 'ok' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#2e9e5b', fontSize: 13.5, marginTop: 8 }}>
          <CheckCircle2 size={15} /> Enregistré
        </div>
      )}
      {etat === 'erreur' && <div style={{ ...encadreErreur, margin: '8px 0 0' }}>{erreur}</div>}
    </div>
  );
};

export const boutonFichier = {
  display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
  border: `1.5px solid ${NAVY}`, color: NAVY, borderRadius: 10, padding: '9px 15px',
  fontSize: 14, fontWeight: 700, background: '#fff'
};

export const encadreErreur = {
  background: '#fdf0f0', border: '1px solid #e0a3a3', color: '#c62828',
  borderRadius: 10, padding: '11px 14px', margin: '14px 0 0', fontSize: 14
};

export const titreEtape = {
  display: 'flex', alignItems: 'center', gap: 10,
  fontWeight: 800, fontSize: 17, color: NAVY, margin: '22px 0 12px'
};

export const numeroEtape = {
  width: 27, height: 27, borderRadius: '50%', background: NAVY, color: '#fff',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
  flexShrink: 0
};

// Dernier bloc de la page : la petite case à cocher et le bouton de validation.
// Rien ne doit être affiché après lui, c'est le point final du parcours.
export const BlocValidation = ({
  peutValider, accepte, setAccepte, erreur, occupe, onValider, avertissement
}) => (
  <div className="dca-card" style={{ marginBottom: 0 }}>
    {avertissement && (
      <div style={{
        display: 'flex', gap: 9, alignItems: 'flex-start', background: '#fdfaf3',
        border: '1px solid #e6c98a', borderRadius: 10, padding: '11px 13px', marginBottom: 14
      }}>
        <AlertCircle size={18} style={{ color: '#b8860b', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 14 }}>{avertissement}</div>
      </div>
    )}

    <label style={{
      display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 14
    }}>
      <input
        type="checkbox"
        checked={accepte}
        onChange={(e) => setAccepte(e.target.checked)}
        style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0, accentColor: NAVY }}
      />
      <span style={{ fontSize: 13.5, color: '#3d4657', lineHeight: 1.45 }}>{TEXTE_ACCEPTATION}</span>
    </label>

    {erreur && <div style={{ ...encadreErreur, margin: '0 0 14px' }}>{erreur}</div>}

    <button
      className="dca-btn"
      onClick={onValider}
      disabled={!peutValider || !accepte || occupe}
      style={{
        width: '100%', justifyContent: 'center', fontSize: 16,
        background: (!peutValider || !accepte) ? '#c9cfe0' : '#2e9e5b', color: '#fff',
        cursor: (!peutValider || !accepte) ? 'not-allowed' : 'pointer'
      }}
    >
      {occupe
        ? <><Loader2 size={18} className="dca-spin" /> Envoi en cours…</>
        : <><CheckCircle2 size={18} /> Valider le choix de ma formule</>}
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Calcul de la paie. Une seule implémentation côté navigateur, reprise à
// l'identique de `calculerRemuneration` du serveur : commission =
// (CA − seuil) × (1 − retenue) × taux, puis on ajoute le fixe.
// ---------------------------------------------------------------------------

export const euros = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2
}).format(Number.isFinite(n) ? n : 0);

export const pourcent = (t) => `${Math.round((t || 0) * 100)} %`;

// Conversion en dirham marocain. Taux forfaitaire de 10, volontairement fixe :
// ce n'est pas le cours réel, c'est la règle de conversion interne demandée.
export const TAUX_MAD = 10;

export const dirhams = (n) => `${new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2
}).format((Number.isFinite(n) ? n : 0) * TAUX_MAD)} DH`;

// Accepte « 10 000 », « 10000,50 », « 10 000 € ». Renvoie null si rien d'exploitable.
export const montantSaisi = (valeur) => {
  const brut = String(valeur == null ? '' : valeur).replace(/\s/g, '').replace(',', '.').replace(/[^\d.]/g, '');
  if (brut === '') return null;
  const n = Number(brut);
  return Number.isFinite(n) ? Math.max(0, n) : null;
};

export const calculerPaie = (remuneration, caTtc) => {
  if (!remuneration) return null;
  const ca = montantSaisi(caTtc);
  if (ca === null) return null;
  const tranche = Math.max(0, ca - remuneration.seuil);
  const base = tranche * (1 - remuneration.retenue);
  const commission = base * remuneration.taux;
  return {
    ca,
    seuil: remuneration.seuil,
    tranche,
    retenue: remuneration.retenue,
    deduction: tranche - base,
    base,
    taux: remuneration.taux,
    commission,
    fixe: remuneration.fixe,
    total: remuneration.fixe + commission
  };
};

export const TEXTE_ACCEPTATION =
  "J'accepte les conditions générales de l'offre et j'atteste avoir lu la formule que j'ai choisie.";
